import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import useAfterIdle from '../utils/useAfterIdle';
import store from '../store';
import runAuthInit from './runAuthInit';
import MainLayout from '../features/shared/layouts/MainLayout';
import ProtectedRoute from '../features/shared/components/ProtectedRoute';
import ScrollToTop from '../components/ScrollToTop';
import SEOHead from '../components/SEO/SEOHead';
import { getFAQSchema } from '../components/SEO/structuredData';
import { HELP_PAGE_FAQ } from '../components/SEO/geoFaqContent';
import { HELP_PAGE_SEO } from '../components/SEO/blogMeta';
import CookieConsent from '../components/CookieConsent';
import GoogleAnalytics from '../components/GoogleAnalytics';
import './App.css';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import ChunkLoadErrorBoundary from './ChunkLoadErrorBoundary';
import NotFoundPage from '../features/shared/pages/NotFoundPage';
import HomePage from '../features/customer/pages/HomePage';
import { PublicPage } from './publicPageRegistry';
import { publicCanonical } from '../components/SEO/siteUrl';

const routeFallback = (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
  </div>
);

// Suspense stays on lazy routes only. The prerendered catalogue is outside it
// so hydrateRoot can attach to the snapshot without Suspense markers.
function lazyRoute(importer) {
  const Page = lazyWithRetry(importer);
  function LazyRoute(props) {
    return (
      <React.Suspense fallback={routeFallback}>
        <Page {...props} />
      </React.Suspense>
    );
  }
  return LazyRoute;
}

// Home and help stay in the main bundle. Contact, about, offers, blogs,
// technician apply, and the service/city catalogue are their own chunks.
// Prerendered HTML preloads that chunk, and index.jsx awaits it before
// hydrateRoot, so the first client render does not show this fallback.
function PrerenderedPublicRoute({ name }) {
  return (
    <React.Suspense fallback={routeFallback}>
      <PublicPage name={name} />
    </React.Suspense>
  );
}

const LoginPage = lazyRoute(() => import('../features/auth/pages/LoginPage'));
const RegisterPage = lazyRoute(() => import('../features/auth/pages/RegisterPage'));
const BookingsPage = lazyRoute(() => import('../features/booking/pages/BookingsPage'));
const NewBookingPage = lazyRoute(() => import('../features/booking/pages/NewBookingPage'));
const BookingConfirmationPage = lazyRoute(() => import('../features/booking/pages/BookingConfirmationPage'));
const RatingPage = lazyRoute(() => import('../features/rating/pages/RatingPage'));
const ProfilePage = lazyRoute(() => import('../features/customer/pages/ProfilePage'));
const SettingsPage = lazyRoute(() => import('../features/customer/pages/SettingsPage'));
const TermsOfServicePage = lazyRoute(() => import('../features/shared/pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazyRoute(() => import('../features/shared/pages/PrivacyPolicyPage'));
const RazorpayTermsPage = lazyRoute(() => import('../features/shared/pages/RazorpayTermsPage'));
const ServicesSearchPage = lazyRoute(() => import('../features/customer/pages/ServicesSearchPage'));
const NotificationsPage = lazyRoute(() => import('../features/notification/pages/NotificationsPage'));

// Lazy load admin pages
const AdminLoginPage = lazyRoute(() => import('../features/admin/pages/AdminLoginPage'));
const AdminDashboardPage = lazyRoute(() => import('../features/admin/pages/AdminDashboardPage'));
const AdminAnalyticsPage = lazyRoute(() => import('../features/admin/pages/AdminAnalyticsPage'));
const AdminNotificationsPage = lazyRoute(() => import('../features/admin/pages/AdminNotificationsPage'));
const ServicesManagementPage = lazyRoute(() => import('../features/admin/pages/ServicesManagementPage'));
const TechnicianManagementPage = lazyRoute(() => import('../features/admin/pages/TechnicianManagementPage'));
const CustomerManagementPage = lazyRoute(() => import('../features/admin/pages/CustomerManagementPage'));
const ContactMessagesPage = lazyRoute(() => import('../features/admin/pages/ContactMessagesPage'));
const AuditLogPage = lazyRoute(() => import('../features/admin/pages/AuditLogPage'));
const ZonesManagementPage = lazyRoute(() => import('../features/admin/pages/ZonesManagementPage'));
const BlogManagementPage = lazyRoute(() => import('../features/admin/pages/BlogManagementPage'));
const WhatsAppInboxPage = lazyRoute(() => import('../features/admin/pages/WhatsAppInboxPage'));
const AdminAppClicksPage = lazyRoute(() => import('../features/admin/pages/AdminAppClicksPage'));
const AdminReviewsPage = lazyRoute(() => import('../features/admin/pages/AdminReviewsPage'));

// Lazy load technician pages
const TechnicianLoginPage = lazyRoute(() => import('../features/technician/pages/TechnicianLoginPage'));
const TechnicianDashboardPage = lazyRoute(() => import('../features/technician/pages/TechnicianDashboardPage'));
const TechnicianNotificationsPage = lazyRoute(() => import('../features/technician/pages/TechnicianNotificationsPage'));
const TechnicianHelpPage = lazyRoute(() => import('../features/technician/pages/TechnicianHelpPage'));
const TechnicianProfilePage = lazyRoute(() => import('../features/technician/pages/TechnicianProfilePage'));
const TechnicianSettingsPage = lazyRoute(() => import('../features/technician/pages/TechnicianSettingsPage'));
const ApplicationSubmittedPage = lazyRoute(() => import('../features/technician/pages/ApplicationSubmittedPage'));
const TechnicianTermsPage = lazyRoute(() => import('../features/technician/pages/TechnicianTermsPage'));

// Help Page Component
const HelpPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = HELP_PAGE_FAQ;

  return (
    <>
      <SEOHead
        title={HELP_PAGE_SEO.title}
        description={HELP_PAGE_SEO.description}
        keywords="Fixome help, Kerala home services, booking FAQ, technician support"
        url={publicCanonical('/help')}
        canonical={publicCanonical('/help')}
        image="/logo_transparent.png"
        structuredData={getFAQSchema(HELP_PAGE_FAQ)}
      />
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">How can we help you?</h1>
          <p className="text-gray-600">Find answers or contact our support team</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <Link
            to="/booking/new/emergency"
            state={{ isEmergency: true, urgency: 'emergency' }}
            className="bg-red-500 text-white p-4 rounded-lg text-center hover:bg-red-600 min-h-[44px]"
          >
            <div className="text-2xl mb-2">🚨</div>
            <div className="font-semibold">Emergency Service</div>
          </Link>
          <a href="/bookings" className="bg-blue-500 text-white p-4 rounded-lg text-center hover:bg-blue-600">
            <div className="text-2xl mb-2">📋</div>
            <div className="font-semibold">My Bookings</div>
          </a>
          <a href="mailto:support@fixome.in" className="bg-green-500 text-white p-4 rounded-lg text-center hover:bg-green-600">
            <div className="text-2xl mb-2">📧</div>
            <div className="font-semibold">Email Support</div>
          </a>
          <a href="tel:+917012689824" className="bg-purple-500 text-white p-4 rounded-lg text-center hover:bg-purple-600">
            <div className="text-2xl mb-2">📞</div>
            <div className="font-semibold">Call Support</div>
          </a>
          <a href="https://wa.me/917012689824" target="_blank" rel="noopener noreferrer" className="bg-green-600 text-white p-4 rounded-lg text-center hover:bg-green-700">
            <div className="text-2xl mb-2">💬</div>
            <div className="font-semibold">WhatsApp</div>
          </a>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-gray-50"
                >
                  <span className="font-medium">{faq.question}</span>
                  <span>{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="px-4 pb-3 text-gray-600 whitespace-pre-line">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <span className="text-2xl mr-3">🚨</span>
            <div>
              <h3 className="font-bold text-red-800">Emergency Support</h3>
              <p className="text-red-700">Emergency line (8am - 7pm): <strong><a href="tel:+917012689824" className="hover:underline">+91 70126 89824</a></strong></p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

/**
 * App Content Component
 * Handles auth initialization and routing
 */
function AppContent() {
  const dispatch = useDispatch();

  useEffect(() => {
    runAuthInit(dispatch);
  }, [dispatch]);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
        <ScrollToTop />
        <CookieConsent />
        <GoogleAnalytics />
        <div className="App">
          <ChunkLoadErrorBoundary>
          <IdleToaster />

          {/* Routes */}
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <MainLayout>
                  <HomePage />
                </MainLayout>
              }
            />

            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />

            {/* Technician Routes */}
            <Route path="/technician/apply" element={<PrerenderedPublicRoute name="technicianApply" />} />
            <Route path="/technician/login" element={<TechnicianLoginPage />} />
            <Route path="/technician/application-submitted" element={<ApplicationSubmittedPage />} />
            <Route path="/technician-terms" element={<TechnicianTermsPage />} />
            
            {/* Technician Protected Routes */}
            <Route
              path="/technician/dashboard"
              element={
                <ProtectedRoute requiredRole="technician">
                  <TechnicianDashboardPage />
                </ProtectedRoute>
              }
            />
            {/* REMOVED: Redundant - using dashboard completion modal instead
            <Route
              path="/technician/jobs"
              element={
                <ProtectedRoute requiredRole="technician">
                  <TechnicianJobsPage />
                </ProtectedRoute>
              }
            />
            */}
            <Route
              path="/technician/profile"
              element={
                <ProtectedRoute requiredRole="technician">
                  <TechnicianProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/technician/settings"
              element={
                <ProtectedRoute requiredRole="technician">
                  <TechnicianSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/technician/notifications"
              element={
                <ProtectedRoute requiredRole="technician">
                  <TechnicianNotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/technician/help"
              element={
                <ProtectedRoute requiredRole="technician">
                  <TechnicianHelpPage />
                </ProtectedRoute>
              }
            />

            {/* Admin Protected Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/services"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ServicesManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/zones"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ZonesManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/technicians"
              element={
                <ProtectedRoute requiredRole="admin">
                  <TechnicianManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/customers"
              element={
                <ProtectedRoute requiredRole="admin">
                  <CustomerManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminNotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/app-clicks"
              element={
                <ProtectedRoute requiredRole={['admin', 'viewer']}>
                  <AdminAppClicksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/contact-messages"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ContactMessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AuditLogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/blogs"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BlogManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/whatsapp"
              element={
                <ProtectedRoute requiredRole="admin">
                  <WhatsAppInboxPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reviews"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminReviewsPage />
                </ProtectedRoute>
              }
            />

            {/* Customer Protected Routes */}
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <BookingsPage />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking/new"
              element={
                <ProtectedRoute>
                  <NewBookingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking/new/:serviceId"
              element={
                <ProtectedRoute>
                  <NewBookingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking/confirmation/:bookingId"
              element={
                <ProtectedRoute>
                  <BookingConfirmationPage />
                </ProtectedRoute>
              }
            />

            {/* Rating Protected Routes */}
            <Route
              path="/bookings/:bookingId/rate"
              element={
                <ProtectedRoute>
                  <RatingPage />
                </ProtectedRoute>
              }
            />

            {/* Notifications Protected Routes */}
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />

            {/* Profile Protected Routes */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Blog Public Routes */}
            <Route path="/blogs" element={<MainLayout><PrerenderedPublicRoute name="blogs" /></MainLayout>} />
            <Route path="/blogs/:slug" element={<MainLayout><PrerenderedPublicRoute name="blogPost" /></MainLayout>} />

            {/* Other Public Routes */}
            <Route path="/services" element={<MainLayout><PrerenderedPublicRoute name="servicesIndex" /></MainLayout>} />
            <Route path="/services/search" element={<ServicesSearchPage />} />
            <Route path="/services/:slug" element={<MainLayout><PrerenderedPublicRoute name="serviceDetail" /></MainLayout>} />
            <Route path="/cities/:slug" element={<MainLayout><PrerenderedPublicRoute name="city" /></MainLayout>} />
            <Route path="/help" element={<MainLayout><HelpPage /></MainLayout>} />
            <Route path="/contact" element={<PrerenderedPublicRoute name="contact" />} />
            <Route path="/about" element={<PrerenderedPublicRoute name="about" />} />
            <Route path="/offers" element={<PrerenderedPublicRoute name="offers" />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/razorpay-terms" element={<RazorpayTermsPage />} />

            {/* Unknown URLs. Vercel returns HTTP 404 outside the rewrite allowlist. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </ChunkLoadErrorBoundary>
        </div>
      </Router>
  );
}

/**
 * Main App Component
 * Provides Redux store and renders app content
 */
function IdleToaster() {
  const ready = useAfterIdle();
  if (!ready) return null;
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 3000,
        style: {
          background: '#363636',
          color: '#fff',
        },
        success: {
          duration: 3000,
          iconTheme: {
            primary: '#10b981',
            secondary: '#fff',
          },
        },
        error: {
          duration: 4000,
          iconTheme: {
            primary: '#D32F2F',
            secondary: '#fff',
          },
        },
      }}
    />
  );
}

const DeferredThirdParties = lazy(() => import('../components/DeferredThirdParties'));

function IdleThirdParties() {
  const ready = useAfterIdle(3500);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <DeferredThirdParties />
    </Suspense>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
      <IdleThirdParties />
    </Provider>
  );
}

export default App;

